import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { referee } from "../../atoms/referee";
import { match } from "../../atoms/match";
import { useRef, useState } from "react";

/* ------------------------------------------------------------------ */
/*  Metric explanation map                                             */
/* ------------------------------------------------------------------ */

const metricDescriptions: Record<string, string> = {
    "Team aggression":
        "(Fouls + Offsides) / Match minutes. Higher values indicate more aggressive play.",
    "Team discipline":
        "Fouls / Cards received. Higher values suggest better discipline (fewer cards per foul).",
    "RSI":
        "Referee Severity Index: Fouls / Cards. Measures how strictly the referee officiates.",
    "Card Rate":
        "(Cards / Fouls) × 10. Shows the referee's propensity to issue cards.",
};

/* ------------------------------------------------------------------ */
/*  Info-icon tooltip component                                        */
/* ------------------------------------------------------------------ */

function InfoTooltip({ text }: { text: string }) {
    const [visible, setVisible] = useState(false);
    const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const show = () => {
        if (hideTimer.current) clearTimeout(hideTimer.current);
        setVisible(true);
    };

    const hide = () => {
        hideTimer.current = setTimeout(() => setVisible(false), 150);
    };

    const toggle = () => setVisible((v) => !v);

    return (
        <span className="relative inline-flex items-center">
      <button
          type="button"
          onClick={toggle}
          onMouseEnter={show}
          onMouseLeave={hide}
          className="flex items-center justify-center w-[16px] h-[16px] rounded-full
                   bg-[#6886BD] dark:bg-[#3B5998] text-white text-[10px] leading-none
                   cursor-pointer select-none hover:bg-[#4f6da3] dark:hover:bg-[#2d4680]
                   transition-colors"
          aria-label="Info"
      >
        i
      </button>

            {visible && (
                <span
                    onMouseEnter={show}
                    onMouseLeave={hide}
                    className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-[6px]
                     w-[220px] px-[10px] py-[8px] rounded-[6px]
                     bg-[#2c3e5a] dark:bg-[#1a2744] text-white text-[12px] leading-[1.4]
                     text-left shadow-lg pointer-events-auto
                     before:content-[''] before:absolute before:top-full before:left-1/2
                     before:-translate-x-1/2 before:border-[5px] before:border-transparent
                     before:border-t-[#2c3e5a] dark:before:border-t-[#1a2744]"
                >
          {text}
        </span>
            )}
    </span>
    );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

interface IRefereeTeamMetrics {
    squad_id: number;
    team_name: string;
    team_aggression: number;
    team_discipline: number;
}

interface IRefereeData {
    match_id: number;
    home_team: {
        logo: string;
        logo_base: string;
    };
    away_team: {
        logo: string;
        logo_base: string;
    };
    referee: {
        rsi: number;
        card_rate: number;
    };
    home: IRefereeTeamMetrics;
    away: IRefereeTeamMetrics;
}

interface StatRow {
    key: string;
    home: string;
    away: string;
}

function safeFormat(v: unknown, decimals: number = 2): string {
    if (typeof v === "number" && !isNaN(v)) return v.toFixed(decimals);
    return "-";
}

function buildRefereeRows(home: IRefereeTeamMetrics, away: IRefereeTeamMetrics): StatRow[] {
    return [
        {
            key: "Team aggression",
            home: safeFormat(home.team_aggression, 2),
            away: safeFormat(away.team_aggression, 2),
        },
        {
            key: "Team discipline",
            home: safeFormat(home.team_discipline, 2),
            away: safeFormat(away.team_discipline, 2),
        },
    ];
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function RefereeSection() {
    const data = useRecoilValue(referee) as IRefereeData | null;
    const matchData = useRecoilValue(match);


    const { t } = useTranslation();

    if (!data?.home || !data?.away || !data?.referee) return null;

    const { home, away, referee: refereeStats } = data;
    const allRows = buildRefereeRows(home, away);

    return (
        <div className="flex-1 rounded-[10px] shadow-teams">

            <div className="bg-[#6886BD99] dark:bg-[#3B599899] py-[5px] px-[24px] text-center text-white text-[16px] font-medium">
                {t("Fouls").toLocaleUpperCase()}
            </div>

            <div className="flex justify-around bg-[#6886BD44] dark:bg-[#3B599844] dark:text-white py-[7px] text-[14px]">
                <div className="flex items-center gap-[6px]">
                    <span className="opacity-75">{t("RSI")}:</span>
                    <span className="font-semibold">{safeFormat(refereeStats.rsi, 2)}</span>
                    {metricDescriptions["RSI"] && (
                        <InfoTooltip text={metricDescriptions["RSI"]} />
                    )}
                </div>
                <div className="flex items-center gap-[6px]">
                    <span className="opacity-75">{t("Card Rate")}:</span>
                    <span className="font-semibold">{safeFormat(refereeStats.card_rate, 2)}</span>
                    {metricDescriptions["Card Rate"] && (
                        <InfoTooltip text={metricDescriptions["Card Rate"]} />
                    )}
                </div>
            </div>

            <div className="flex justify-around bg-[#6886BD44] dark:bg-[#3B599844] dark:text-white py-[7px]">
                <div className="flex items-center gap-[10px]">
                    <img
                        className="w-[30px] h-[35px]"
                        src={`${matchData.home_team.logo_base || ""}/${matchData.home_team.logo || ""}`}
                        alt=""
                    />
                    <p>{home.team_name || ""}</p>
                </div>
                <div className="flex items-center gap-[10px]">
                    <img
                        className="w-[30px] h-[35px]"
                        src={`${matchData.away_team.logo_base || ""}/${matchData.away_team.logo || ""}`}
                        alt=""
                    />
                    <p>{away.team_name || ""}</p>
                </div>
            </div>

            <table className="w-full text-center dark:text-white dark:bg-[#1B2436]">
                <tbody>
                {allRows.map((row) => (
                    <tr
                        key={row.key}
                        className="border-b border-[#6886BD] dark:border-[#3B5998]"
                    >
                        <td className="w-[100px] py-[4px]">{row.home}</td>
                        <td className="py-[4px] relative">
                            {t(row.key)}
                            {metricDescriptions[row.key] && (
                                <span className="absolute right-[8px] top-1/2 -translate-y-1/2">
                    <InfoTooltip text={metricDescriptions[row.key]} />
                  </span>
                            )}
                        </td>
                        <td className="w-[100px] py-[4px]">{row.away}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}
